import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Shield, Award, Users, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import QuoteModal from "@/components/QuoteModal";
import SEO from "@/components/SEO";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=2000&q=80";

const stats = [
  { value: "3+", label: "Années d'Expérience" },
  { value: "100+", label: "Projets Réalisés" },
  { value: "50+", label: "Clients Satisfaits" },
  { value: "2", label: "Sites de Production" },
];

const features = [
  {
    icon: Shield,
    title: "Qualité Certifiée",
    description: "Nos matériaux répondent aux normes les plus strictes du secteur BTP.",
  },
  {
    icon: Truck,
    title: "Livraison Rapide",
    description: "Service de livraison sur tout le territoire sénégalais.",
  },
  {
    icon: Award,
    title: "Expertise Locale",
    description: "Production 100% sénégalaise avec une connaissance du marché local.",
  },
  {
    icon: Users,
    title: "Accompagnement",
    description: "Une équipe dédiée pour vous conseiller dans vos projets.",
  },
];

export default function HomePage() {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Seed products first
        await axios.post(`${API}/seed-products`);
        // Then fetch
        const response = await axios.get(`${API}/products`);
        setProducts(response.data.slice(0, 4));
      } catch (error) {
        console.error("Erreur lors du chargement des produits:", error);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title="Agglos, Parpaings, Briques & Granulats de Silex"
        description="AGRABAT Sénégal - Fabricant d'agglos, parpaings, briques, béton et granulats de silex. Matériaux de construction de qualité. Livraison Thiès, Dakar, Gambie. Cailloux et roches pour BTP."
        keywords="agglo, parpaing, brique, béton, granulats, silex, cailloux, roche, matériaux construction, Sénégal, Thiès, Dakar, Gambie, AGRABAT, hourdis, fondation, BTP"
        url="https://agrabat.sn/"
      />
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center md:text-left">
          <div className="max-w-3xl">
            <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4 animate-fade-in">
              Matériaux de Construction au Sénégal
            </p>
            <h1 className="font-oswald text-5xl md:text-7xl font-bold text-white uppercase tracking-tight mb-6 animate-fade-in-up">
              Granulats de Silex<br />
              <span className="text-[#FFD100]">& Agglos</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-xl animate-fade-in-up animation-delay-200">
              AGRABAT, votre partenaire de confiance pour des matériaux de construction de qualité supérieure. Production locale, qualité internationale.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start animate-fade-in-up animation-delay-300">
              <Button
                onClick={() => setIsQuoteModalOpen(true)}
                className="bg-[#FFD100] text-[#1E3A5F] hover:bg-[#E6BC00] rounded-none px-8 py-6 text-lg font-bold uppercase tracking-wider"
                data-testid="hero-quote-btn"
              >
                Demander un Devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Link to="/produits">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#1E3A5F] rounded-none px-8 py-6 text-lg font-bold uppercase tracking-wider"
                  data-testid="hero-products-btn"
                >
                  Nos Produits
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#1E3A5F] py-12" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item text-center px-4">
                <p className="font-oswald text-4xl md:text-5xl font-bold text-[#FFD100] mb-2">
                  {stat.value}
                </p>
                <p className="text-white/80 text-sm uppercase tracking-wider">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
              Pourquoi AGRABAT
            </p>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold text-[#1E3A5F] uppercase tracking-tight">
              Nos Engagements
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="feature-card p-8 bg-white border-l-4 border-[#1E3A5F] hover:border-[#FFD100] transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <feature.icon className="w-12 h-12 text-[#FFD100] mb-4" />
                <h3 className="font-oswald text-xl font-semibold text-[#1E3A5F] mb-3 uppercase">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="py-24 bg-white" data-testid="products-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-16">
            <div>
              <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
                Notre Catalogue
              </p>
              <h2 className="font-oswald text-4xl md:text-5xl font-bold text-[#1E3A5F] uppercase tracking-tight">
                Nos Produits
              </h2>
            </div>
            <Link to="/produits" className="mt-6 md:mt-0">
              <Button
                variant="outline"
                className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-none px-6 py-3 font-bold uppercase tracking-wider"
                data-testid="view-all-products-btn"
              >
                Voir Tout
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div
                key={product.id}
                className="product-card group relative overflow-hidden bg-white border border-slate-200 hover:border-[#FFD100]"
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="product-image w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs text-[#FFD100] uppercase tracking-widest font-medium">
                    {product.category === "granulats" ? "Granulats" : "Agglos"}
                  </span>
                  <h3 className="font-oswald text-lg font-semibold text-[#1E3A5F] mt-2 uppercase">
                    {product.name}
                  </h3>
                  <p className="text-slate-600 text-sm mt-2 line-clamp-2">
                    {product.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24 bg-[#1E3A5F] relative overflow-hidden"
        data-testid="cta-section"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFD100] transform rotate-45 translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FFD100] transform rotate-45 -translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-oswald text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
            Prêt à Démarrer<br />
            <span className="text-[#FFD100]">Votre Projet ?</span>
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Contactez-nous dès maintenant pour obtenir un devis personnalisé adapté à vos besoins en matériaux de construction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setIsQuoteModalOpen(true)}
              className="bg-[#FFD100] text-[#1E3A5F] hover:bg-[#E6BC00] rounded-none px-8 py-6 text-lg font-bold uppercase tracking-wider"
              data-testid="cta-quote-btn"
            >
              Demander un Devis Gratuit
            </Button>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#1E3A5F] rounded-none px-8 py-6 text-lg font-bold uppercase tracking-wider"
                data-testid="cta-contact-btn"
              >
                Nous Contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} />
    </div>
  );
}
